import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, ChevronDown, ChevronRight, Pencil, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

// Sortable row for scope items
function SortableScopeRow({ item, index, onRemove, onEdit, rateLabel }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._sortId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
    backgroundColor: isDragging ? '#fef3c7' : undefined,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? 'shadow-lg' : ''}>
      <TableCell className="w-8">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-200 rounded touch-none"
          title="Drag to reorder"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </button>
      </TableCell>
      <TableCell className="font-medium">{item.service || '—'}</TableCell>
      <TableCell className="text-slate-600">{item.custom_description || item.description || '—'}</TableCell>
      <TableCell className="text-right">{item.quantity}</TableCell>
      <TableCell className="text-right text-xs text-slate-500">{rateLabel(item.rate_type)}</TableCell>
      <TableCell className="text-right whitespace-nowrap">${item.unit_price?.toFixed(2) || '0.00'}</TableCell>
      <TableCell className="text-right font-medium whitespace-nowrap">${item.total?.toFixed(2) || '0.00'}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(item, index)} className="text-blue-600 hover:bg-blue-50">
            <Pencil className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function ScopeItemsList({ items, scopeIndex, onItemsChange, onEdit, rateLabel }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const itemsWithIds = items.map((item, i) => ({
    ...item,
    _sortId: item.id || `scope-${scopeIndex}-item-${i}-${Date.now()}`
  }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = itemsWithIds.findIndex(item => item._sortId === active.id);
      const newIndex = itemsWithIds.findIndex(item => item._sortId === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(itemsWithIds, oldIndex, newIndex);
        onItemsChange(reordered.map(({ _sortId, ...rest }) => rest));
      }
    }
  };

  const handleRemove = (index) => {
    onItemsChange(items.filter((_, i) => i !== index));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Type</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <SortableContext items={itemsWithIds.map(item => item._sortId)} strategy={verticalListSortingStrategy}>
            <TableBody>
              {itemsWithIds.map((item, index) => (
                <SortableScopeRow
                  key={item._sortId}
                  item={item}
                  index={index}
                  onRemove={handleRemove}
                  onEdit={onEdit}
                  rateLabel={rateLabel}
                />
              ))}
            </TableBody>
          </SortableContext>
        </Table>
        {items.length > 1 && (
          <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
            <GripVertical className="w-3 h-3" /> Drag rows to reorder
          </p>
        )}
      </div>
    </DndContext>
  );
}

export default function ProposalScopeSection({ scope, scopeIndex, services, onUpdate, onRemove }) {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [customDesc, setCustomDesc] = useState('');
  const [itemQty, setItemQty] = useState('1');
  const [itemPrice, setItemPrice] = useState('0');
  const [itemRateType, setItemRateType] = useState('hour');
  const [editingIndex, setEditingIndex] = useState(null);

  const scopeSubtotal = (scope.items || []).reduce((sum, item) => sum + (item.total || 0), 0);

  const addItem = () => {
    let serviceName = '';
    let customDescription = '';

    if (selectedService === 'Other') {
      if (!customDesc.trim()) { toast.error('Please enter a description'); return; }
      serviceName = 'Other';
      customDescription = customDesc.trim();
    } else if (selectedService) {
      serviceName = selectedService;
      customDescription = customDesc.trim();
    } else {
      toast.error('Please select a service'); return;
    }

    const qty = parseFloat(itemQty) || 0;
    const price = parseFloat(itemPrice) || 0;

    const newItem = {
      service: serviceName,
      description: customDescription || '',
      custom_description: customDescription,
      quantity: qty,
      unit_price: price,
      total: qty * price,
      rate_type: itemRateType
    };

    const updatedItems = [...(scope.items || [])];
    if (editingIndex !== null) {
      updatedItems[editingIndex] = { ...updatedItems[editingIndex], ...newItem };
      setEditingIndex(null);
      toast.success('Line updated');
    } else {
      updatedItems.push(newItem);
    }

    onUpdate({ ...scope, items: updatedItems });
    resetForm();
  };

  const resetForm = () => {
    setSelectedService('');
    setCustomDesc('');
    setItemQty('1');
    setItemPrice('0');
    setItemRateType('hour');
    setEditingIndex(null);
  };

  const handleEditItem = (item, index) => {
    setSelectedService(item.service || '');
    setCustomDesc(item.custom_description || '');
    setItemQty(item.quantity?.toString() || '1');
    setItemPrice(item.unit_price?.toString() || '0');
    setItemRateType(item.rate_type || 'hour');
    setEditingIndex(index);
    setCollapsed(false);
  };

  const removeItem = (index) => {
    const updatedItems = (scope.items || []).filter((_, i) => i !== index);
    onUpdate({ ...scope, items: updatedItems });
  };

  const rateLabel = (rt) =>
    rt === 'hour' ? '/hr' : rt === 'day' ? '/day' : rt === 'night' ? '/night' : rt === 'hole' ? '/hole' : rt === 'linear_footage' ? '/LF' : 'fixed';

  return (
    <div className="border-2 border-slate-300 rounded-lg overflow-hidden" data-testid={`scope-section-${scopeIndex}`}>
      {/* Scope Header */}
      <div className="bg-slate-100 px-3 py-2 flex items-center gap-2">
        <button type="button" onClick={() => setCollapsed(!collapsed)} className="p-1 hover:bg-slate-200 rounded">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <Input
          value={scope.name}
          onChange={(e) => onUpdate({ ...scope, name: e.target.value })}
          placeholder="Scope Name (e.g., Red Scope)"
          className="flex-1 bg-white font-semibold"
          data-testid={`scope-name-input-${scopeIndex}`}
        />
        <span className="text-sm font-medium text-slate-600 whitespace-nowrap">
          ${scopeSubtotal.toFixed(2)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(scopeIndex)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
          data-testid={`remove-scope-btn-${scopeIndex}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Scope Body */}
      {!collapsed && (
        <div className="p-3 space-y-3">
          {/* Add item form */}
          <div className={`space-y-2 p-2 rounded border transition-colors ${editingIndex !== null ? 'border-blue-400 bg-blue-50/50' : 'border-transparent'}`}>
            {editingIndex !== null && (
              <div className="text-xs text-blue-600 font-medium flex items-center gap-1">
                <Pencil className="w-3 h-3" /> Editing Line {editingIndex + 1}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Service</Label>
                <Select value={selectedService} onValueChange={(value) => {
                  setSelectedService(value);
                  const service = services.find(s => s.name === value);
                  if (service) {
                    if (itemRateType === 'day' && service.day_price) setItemPrice(parseFloat(service.day_price) || 0);
                    else if (itemRateType === 'hour' && service.hourly_price) setItemPrice(parseFloat(service.hourly_price) || 0);
                    else if (itemRateType === 'fixed' && service.fixed_price) setItemPrice(parseFloat(service.fixed_price) || 0);
                    else if (itemRateType === 'night' && service.night_price) setItemPrice(parseFloat(service.night_price) || 0);
                  }
                }}>
                  <SelectTrigger className="bg-white w-full" data-testid={`scope-${scopeIndex}-service-select`}>
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map(s => (
                      <SelectItem key={s.id || s.name} value={s.name}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{selectedService === 'Other' ? 'Description *' : 'Description'}</Label>
                <Input value={customDesc} onChange={(e) => setCustomDesc(e.target.value)}
                  placeholder={selectedService === 'Other' ? "Enter description" : "Optional description"} className="bg-white" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Rate Type</Label>
                {(() => {
                  const currentService = services.find(s => s.name === selectedService);
                  const hasPerHole = currentService?.per_hole_price;
                  return (
                    <Select value={itemRateType} onValueChange={(value) => {
                      setItemRateType(value);
                      const service = services.find(s => s.name === selectedService);
                      if (service) {
                        if (value === 'day' && service.day_price) setItemPrice(parseFloat(service.day_price) || 0);
                        else if (value === 'hour' && service.hourly_price) setItemPrice(parseFloat(service.hourly_price) || 0);
                        else if (value === 'hole' && service.per_hole_price) setItemPrice(parseFloat(service.per_hole_price) || 0);
                        else if (value === 'fixed' && service.fixed_price) setItemPrice(parseFloat(service.fixed_price) || 0);
                        else if (value === 'night' && service.night_price) setItemPrice(parseFloat(service.night_price) || 0);
                      }
                    }}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hour">Per Hour</SelectItem>
                        <SelectItem value="day">Per Day</SelectItem>
                        <SelectItem value="night">Per Night</SelectItem>
                        {hasPerHole && <SelectItem value="hole">Per Hole</SelectItem>}
                        <SelectItem value="linear_footage">Linear Footage</SelectItem>
                        <SelectItem value="fixed">Fixed</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Qty</Label>
                <Input type="number" min="0" step="0.5" value={itemQty} onChange={(e) => setItemQty(e.target.value)} className="bg-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Price ($)</Label>
                <Input type="number" step="0.01" value={itemPrice} onChange={(e) => setItemPrice(e.target.value)} className="bg-white" />
              </div>
            </div>
          </div>

          {/* Items list with drag-to-reorder */}
          {(scope.items || []).length > 0 && (
            <ScopeItemsList
              items={scope.items || []}
              scopeIndex={scopeIndex}
              onItemsChange={(newItems) => onUpdate({ ...scope, items: newItems })}
              onEdit={handleEditItem}
              rateLabel={rateLabel}
            />
          )}

          {/* Add/Update button */}
          {editingIndex !== null ? (
            <div className="flex gap-2">
              <Button onClick={addItem} variant="default" size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700">
                <Pencil className="w-3 h-3 mr-1" /> Update Line
              </Button>
              <Button onClick={resetForm} variant="outline" size="sm">Cancel</Button>
            </div>
          ) : (
            <Button onClick={addItem} variant="outline" size="sm" className="w-full" data-testid={`scope-${scopeIndex}-add-item-btn`}>
              <Plus className="w-3 h-3 mr-1" /> Add Service Line
            </Button>
          )}

          {/* Scope subtotal */}
          {(scope.items || []).length > 0 && (
            <div className="flex justify-end text-sm font-semibold text-slate-700 border-t pt-2">
              <span className="mr-4">Scope Total:</span>
              <span>${scopeSubtotal.toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
